import InfoHelp from "../../components/help/InfoHelp";
import { todaySaoPaulo } from "../../utils/format";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { TrainingLookup } from "./TrainingLookup";
import { TrainingValidityPanel } from "./TrainingValidityPanel";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import Header from "../../components/layout/Header";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Award, Settings2, BookOpen, Calendar, Clock, Upload, Download, FileArchive } from "lucide-react";

const EMPTY_CFG = {
  emitir_certificado: true,
  duracao_minutos: 15,
  nome: "",
  sigla: "",
  norma: "",
  carga_horaria_total: 8,
  horas_max_por_dia: 4,
  conteudo_programatico: "",
  observacoes: "",
  validade_meses: 12,
};

function ConfigsTab() {
  const [configs, setConfigs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_CFG);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    try { const r = await api.get("/treinamentos/configs"); setConfigs(r.data); }
    catch { toast.error("Falha ao carregar treinamentos"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY_CFG); setEditingId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...EMPTY_CFG, ...c }); setEditingId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Informe o nome");
    try {
      if (editingId) {
        await api.put(`/treinamentos/configs/${editingId}`, form);
        toast.success("Atualizado");
      } else {
        await api.post("/treinamentos/configs", form);
        toast.success("Criado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir "${c.nome}"?`)) return;
    try { await api.delete(`/treinamentos/configs/${c.id}`); toast.success("Excluído"); load(); }
    catch { toast.error("Falha"); }
  };

  const nDays = form.emitir_certificado ? Math.max(1, Math.ceil((form.carga_horaria_total || 0) / (form.horas_max_por_dia || 1))) : 1;

  return (
    <div className="space-y-4" data-testid="treinamentos-configs-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Modelos de treinamento</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Ajuste conteúdo, carga horária e validade apenas quando precisar alterar um modelo.
          </p>
        </div>
        <Button onClick={openNew} data-testid="treinamento-new-btn"><Plus className="w-4 h-4 mr-1" /> Novo modelo</Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {configs.map((c) => (
          <div key={c.id} className="border border-border rounded-xl p-4 space-y-2 bg-card" data-testid={`treinamento-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold flex flex-wrap items-center gap-2">
                  {c.nome}
                  {c.sigla && <Badge variant="outline">{c.sigla}</Badge>}
                </div>
                {c.norma && <div className="text-xs text-muted-foreground">{c.norma}</div>}
              </div>
              <div className="flex shrink-0 gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`treinamento-edit-${c.id}`} aria-label={`Editar ${c.nome}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`treinamento-delete-${c.id}`} aria-label={`Excluir ${c.nome}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {c.emitir_certificado === false ? `${c.duracao_minutos || 15} min` : `${c.carga_horaria_total}h total`}</span>
              {c.emitir_certificado !== false && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> máx {c.horas_max_por_dia}h/dia</span>}
              <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> {c.emitir_certificado === false ? 1 : Math.max(1, Math.ceil(c.carga_horaria_total / c.horas_max_por_dia))} dia(s)</span>
            </div>
            {c.emitir_certificado === false && <Badge variant="secondary">Somente presença · {c.duracao_minutos || 15} min</Badge>}
            {c.conteudo_programatico && (
              <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap pt-1">
                {c.conteudo_programatico}
              </div>
            )}
          </div>
        ))}
        {configs.length === 0 && (
          <div className="md:col-span-2 text-center text-sm text-muted-foreground py-16 border border-dashed border-border rounded-xl">
            Nenhum treinamento cadastrado. Clique em "Novo modelo" para começar.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="treinamento-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar treinamento" : "Novo treinamento"}</DialogTitle>
            <DialogDescription>Preencha os parâmetros do treinamento. O conteúdo será usado nos documentos gerados.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Nome do treinamento *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Ex.: CIPA" data-testid="treinamento-form-nome" />
            </div>
            <div>
              <Label>Sigla</Label>
              <Input value={form.sigla || ""} onChange={(e) => setForm({ ...form, sigla: e.target.value })} placeholder="NR-05" data-testid="treinamento-form-sigla" />
            </div>
            <div>
              <Label>Norma de referência</Label>
              <Input value={form.norma || ""} onChange={(e) => setForm({ ...form, norma: e.target.value })} placeholder="NR-05 Anexo II" data-testid="treinamento-form-norma" />
            </div>
            <label className="sm:col-span-2 flex items-center gap-2 rounded-lg border p-3"><input type="checkbox" checked={form.emitir_certificado} onChange={(e) => setForm({ ...form, emitir_certificado: e.target.checked })} /> Emitir certificado além da lista de presença</label>
            {!form.emitir_certificado && <div className="sm:col-span-2"><Label>Duração da atividade (minutos)</Label><Input type="number" min="1" max="1440" value={form.duracao_minutos} onChange={(e) => setForm({ ...form, duracao_minutos: Number(e.target.value) })} /><p className="text-xs text-muted-foreground mt-1">Para DDS e outras atividades: gera somente a lista de presença e guarda o registro, sem vencimento de certificado.</p></div>}
            <div hidden={!form.emitir_certificado}>
              <Label>Carga horária total (h) *</Label>
              <Input type="number" min="1" value={form.carga_horaria_total} onChange={(e) => setForm({ ...form, carga_horaria_total: parseInt(e.target.value || 1, 10) })} data-testid="treinamento-form-carga" />
            </div>
            <div hidden={!form.emitir_certificado}>
              <Label>Horas máximas por dia *</Label>
              <Input type="number" min="1" max="24" value={form.horas_max_por_dia} onChange={(e) => setForm({ ...form, horas_max_por_dia: parseInt(e.target.value || 1, 10) })} data-testid="treinamento-form-max-dia" />
            </div>
            <div className="sm:col-span-2" hidden={!form.emitir_certificado}>
              <div className="max-w-sm">
                <Label>Validade do treinamento</Label>
                <select
                  value={form.validade_meses}
                  onChange={(e) => setForm({ ...form, validade_meses: parseInt(e.target.value, 10) })}
                  className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                  data-testid="treinamento-form-validade"
                >
                  <option value={12}>1 ano (12 meses)</option>
                  <option value={24}>2 anos (24 meses)</option>
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">Usado para calcular os próximos vencimentos.</p>
              </div>
            </div>
            <div hidden={!form.emitir_certificado} className="sm:col-span-2 text-xs text-muted-foreground bg-muted/30 rounded p-2">
              Duração calculada: <span className="font-mono text-foreground font-semibold">{nDays} dia(s)</span> ({form.carga_horaria_total}h ÷ {form.horas_max_por_dia}h/dia, arredondado para cima).
            </div>
            <div className="sm:col-span-2">
              <Label>Conteúdo programático</Label>
              <Textarea rows={8} value={form.conteudo_programatico} onChange={(e) => setForm({ ...form, conteudo_programatico: e.target.value })} placeholder="Módulo 1 - ...&#10;Módulo 2 - ..." data-testid="treinamento-form-conteudo" />
            </div>
            <div className="sm:col-span-2">
              <Label>Observações</Label>
              <Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="treinamento-form-obs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="treinamento-form-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmissaoTab() {
  const { readOnly } = useAuth();
  const [params, setParams] = useSearchParams();
  const contextClientId = params.get("client_id") || "";
  const renewalId = params.get("renewal_of") || "";
  const renewalParticipant = params.get("participant") || "";
  const [renewalOf, setRenewalOf] = useState("");
  const [renewalLoading, setRenewalLoading] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [clienteId, setClienteId] = useState("");
  const [treinamentoId, setTreinamentoId] = useState("");
  const [empresa, setEmpresa] = useState("");
  const [municipioEmpresa, setMunicipioEmpresa] = useState("");
  const [municipioEmissao, setMunicipioEmissao] = useState("");
  const [grauRisco, setGrauRisco] = useState("");
  const [modoDias, setModoDias] = useState("uteis");
  const [dataInicio, setDataInicio] = useState(todaySaoPaulo());
  const [datasManuais, setDatasManuais] = useState([]);
  const [horariosPorDia, setHorariosPorDia] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcionarioManual, setFuncionarioManual] = useState("");
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [incluirRastreio, setIncluirRastreio] = useState(false);
  const [jobResult, setJobResult] = useState(null);
  const [multi, setMulti] = useState(false);
  const [blankDates, setBlankDates] = useState(false);
  const [checkedCourses, setCheckedCourses] = useState([]);
  const [basket, setBasket] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [presenceRecords, setPresenceRecords] = useState([]);
  const [completion, setCompletion] = useState(null);
  const [completionDates, setCompletionDates] = useState([]);
  const [completionHours, setCompletionHours] = useState([]);
  const [completionTracking, setCompletionTracking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const completionKey = useRef(null);
  const batchKey = useRef(null);
  const emitting = useRef(false);
  const refreshPresence = useCallback(() => api.get("/treinamentos/presencas").then(({ data }) => setPresenceRecords(data)).catch(() => toast.error("Não foi possível consultar os registros de presença")), []);
  useEffect(() => { refreshPresence(); }, [refreshPresence]);
  const [instrutorNome, setInstrutorNome] = useState("CLEBER RIBEIRO DE MORAES");
  const [instrutorCred, setInstrutorCred] = useState(
    "Especialista em QSMS\nTécnico em Eletrotécnica CRT/RJ 00759148759\nTécnico em Segurança do Trabalho   CREA RJ 2018127304"
  );

  useEffect(() => {
    if (!renewalId) { setRenewalOf(""); return; }
    let cancelled = false;
    setRenewalLoading(true);
    api.get(`/treinamentos/records/${encodeURIComponent(renewalId)}/renewal`).then(({ data }) => {
      if (cancelled) return;
      setConfigs([data.course]); setTreinamentoId(data.course.id);
      setClientes(data.client ? [data.client] : []); setClienteId(data.client?.id || "");
      setEmpresa(data.client?.razao_social || data.empresa_cliente || "");
      setMunicipioEmpresa(data.client?.municipio || ""); setMunicipioEmissao(data.client?.municipio || "");
      setGrauRisco(data.client?.grau_risco ? String(data.client.grau_risco) : "");
      setFuncionarios(renewalParticipant ? data.participantes.filter((name) => name === renewalParticipant) : data.participantes);
      setRenewalOf(data.renewal_of);
    }).catch((e) => { if (!cancelled) toast.error(e.response?.data?.detail || "Não foi possível preparar a renovação"); })
      .finally(() => { if (!cancelled) setRenewalLoading(false); });
    return () => { cancelled = true; };
  }, [renewalId, renewalParticipant]);

  useEffect(() => {
    if (!multi || !blankDates) return;
    api.get("/treinamentos/configs").then(({ data }) => setConfigs(data)).catch(() => toast.error("Não foi possível carregar os treinamentos"));
  }, [multi, blankDates]);
  useEffect(() => {
    if (!contextClientId || renewalId) return;
    let cancelled = false;
    api.get(`/clients/${encodeURIComponent(contextClientId)}`).then(({ data }) => {
      if (cancelled) return;
      setClientes([data]); setClienteId(data.id); setEmpresa(data.razao_social || "");
      setMunicipioEmpresa(data.municipio || data.cidade || ""); setGrauRisco(data.grau_risco ? String(data.grau_risco) : "");
    }).catch(() => toast.error("Não foi possível selecionar o cliente. Escolha-o antes de gerar."));
    return () => { cancelled = true; };
  }, [contextClientId, renewalId]);
  const selectionCount = blankDates ? checkedCourses.length : basket.length;
  const selectedCfg = configs.find((c) => c.id === treinamentoId);
  const isCipa = selectedCfg && selectedCfg.emitir_certificado !== false && ((selectedCfg.sigla || "").toUpperCase().trim() === "NR-05" || (selectedCfg.nome || "").toUpperCase().includes("CIPA"));
  const CIPA_HOURS = { 1: 8, 2: 12, 3: 16, 4: 20 };
  const effCarga = isCipa && grauRisco && CIPA_HOURS[+grauRisco] ? CIPA_HOURS[+grauRisco] : (selectedCfg ? selectedCfg.carga_horaria_total : 0);
  const presenceOnly = selectedCfg?.emitir_certificado === false;
  const nDays = presenceOnly ? 1 : selectedCfg ? Math.max(1, Math.ceil(effCarga / (isCipa ? Math.min(selectedCfg.horas_max_por_dia, 8) : selectedCfg.horas_max_por_dia))) : 0;

  const onClienteChange = (id) => {
    setClienteId(id);
    const c = clientes.find((x) => x.id === id);
    if (c) {
      setEmpresa(c.razao_social || "");
      setMunicipioEmpresa(c.municipio || c.cidade || "");
      if (c.grau_risco) setGrauRisco(String(c.grau_risco));
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/treinamentos/upload-funcionarios", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFuncionarios(r.data.funcionarios);
      toast.success(`${r.data.total} funcionário(s) importado(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao ler planilha");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const addFuncionarioManual = () => {
    const v = funcionarioManual.trim();
    if (!v) return;
    setFuncionarios((prev) => [...prev, v]);
    setFuncionarioManual("");
  };

  const removeFuncionario = (idx) => setFuncionarios((prev) => prev.filter((_, i) => i !== idx));

  const updateDataManual = (idx, value) => {
    const arr = [...datasManuais];
    arr[idx] = value;
    setDatasManuais(arr);
  };

  useEffect(() => {
    if (modoDias === "manual" && nDays > 0 && datasManuais.length !== nDays) {
      setDatasManuais((prev) => {
        const arr = [...prev];
        while (arr.length < nDays) arr.push("");
        arr.length = nDays;
        return arr;
      });
    }
  }, [modoDias, nDays]); // eslint-disable-line

  useEffect(() => {
    if (nDays > 0 && horariosPorDia.length !== nDays) {
      setHorariosPorDia((prev) => {
        const arr = [...prev];
        while (arr.length < nDays) arr.push({ inicio: "", fim: "" });
        arr.length = nDays;
        return arr;
      });
    }
  }, [nDays]); // eslint-disable-line

  const updHorario = (idx, field, value) => {
    const arr = [...horariosPorDia];
    arr[idx] = { ...(arr[idx] || { inicio: "", fim: "" }), [field]: value };
    setHorariosPorDia(arr);
  };

  const prepare = (courseId = treinamentoId) => {
    if (!courseId) throw new Error("Selecione um treinamento");
    if (!empresa.trim()) throw new Error("Informe a empresa cliente");
    if (!instrutorNome.trim()) throw new Error("Informe o responsável pelo treinamento");
    if (!funcionarios.length) throw new Error("Adicione participantes");
    if (!blankDates && modoDias === "uteis" && !dataInicio) throw new Error("Informe a data de início");
    if (!blankDates && modoDias === "manual" && (datasManuais.length !== nDays || datasManuais.some((d) => !d))) throw new Error(`Preencha todas as ${nDays} datas`);
    const course = configs.find((c) => c.id === courseId);
    const courseCipa = course?.emitir_certificado !== false && ((course?.sigla || "").toUpperCase().trim() === "NR-05" || (course?.nome || "").toUpperCase().includes("CIPA"));
    if (courseCipa && !grauRisco) throw new Error(`Informe o grau de risco para ${course.nome}`);
    return { treinamento_id: courseId, deixar_datas_em_branco: blankDates, incluir_rastreio: presenceOnly || blankDates ? false : incluirRastreio,
      renewal_of: renewalOf || null, empresa_cliente: empresa, client_id: clienteId || null,
      municipio_empresa: municipioEmpresa, municipio_emissao: municipioEmissao || municipioEmpresa,
      modo_dias: modoDias, data_inicio: !blankDates && modoDias === "uteis" ? dataInicio : null,
      datas_manuais: !blankDates && modoDias === "manual" ? [...datasManuais] : [],
      horarios_por_dia: blankDates ? [] : horariosPorDia.map((h) => ({ ...h })), funcionarios: [...funcionarios],
      instrutor_nome: instrutorNome, instrutor_credenciais: instrutorCred,
      client_grau_risco: grauRisco ? Number(grauRisco) : null };
  };
  const addToBasket = () => {
    try {
      if (basket.length >= 20) throw new Error("Limite de 20 treinamentos por lote");
      const payload = prepare();
      if (basket.some((item) => JSON.stringify(item.payload) === JSON.stringify(payload))) throw new Error("Este treinamento já está na seleção com os mesmos dados");
      setBasket((old) => [...old, { nome: selectedCfg.nome, presenceOnly, payload }]);
      batchKey.current = null; setBatchResult(null); setTreinamentoId(""); setIncluirRastreio(false);
      toast.success("Treinamento selecionado. Escolha o próximo e confira suas datas.");
    } catch (e) { toast.error(e.message); }
  };
  const emitir = async () => {
    if (emitting.current) return;
    let payload;
    let batchPayloads = [];
    try { payload = multi ? null : prepare(); if (multi) batchPayloads = blankDates ? checkedCourses.map((id) => prepare(id)) : basket.map((item) => item.payload); if (multi && !batchPayloads.length) throw new Error("Adicione treinamentos à seleção"); }
    catch (e) { return toast.error(e.message); }
    if (!window.confirm(multi ? `Gerar os ${selectionCount} treinamentos selecionados? Confira as datas e os participantes de cada item na seleção.` : `Gerar ${presenceOnly ? "somente listas de presença" : "certificados e listas"} de ${selectedCfg.nome} para ${empresa}, com ${funcionarios.length} participantes?`)) return;
    emitting.current = true; setGenerating(true); setJobResult(null);
    try {
      if (multi) {
        batchKey.current ||= crypto.randomUUID();
        const { data } = await api.post("/treinamentos/emitir-lote", { request_id: batchKey.current, treinamentos: batchPayloads });
        setBatchResult(data);
        if (data.complete) toast.success("Todos os treinamentos foram gerados. Baixe o pacote único.");
        else toast.error("Geração interrompida. Os itens concluídos foram preservados; confira o resultado antes de repetir.");
      } else {
        const { data } = await api.post("/treinamentos/emitir", payload);
        setJobResult(data); toast.success(data.preparado ? "Documentos preparados para preenchimento à mão" : data.cert_filename ? "Certificados e listas gerados" : "Listas de presença geradas, sem certificados");
      }
      setIncluirRastreio(false); refreshPresence();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.item ? `Treinamento ${detail.item}: ${detail.message}` : detail?.message) || "Não foi possível concluir. Confira o histórico antes de repetir.");
    } finally { emitting.current = false; setGenerating(false); }
  };
  const downloadPackage = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível baixar o pacote"); }
  };

  const openCompletion = async (record) => {
    try {
      const { data } = await api.get(`/treinamentos/preparacoes/${record.job_id}`);
      setCompletion(data); setCompletionDates(Array(data.horas_por_dia.length).fill(""));
      setCompletionHours(data.horas_por_dia.map(() => ({ inicio: "", fim: "" })));
      setCompletionTracking(false); completionKey.current = crypto.randomUUID();
    } catch { toast.error("Não foi possível abrir o treinamento preparado"); }
  };
  const completeTraining = async () => {
    if (completing || !completion) return;
    if (completionDates.some((d) => !d)) return toast.error("Informe a data de cada dia do treinamento");
    setCompleting(true);
    try {
      const { data } = await api.post(`/treinamentos/preparacoes/${completion.job_id}/concluir`, {
        request_id: completionKey.current, modo_dias: "manual", datas_manuais: completionDates,
        horarios_por_dia: completionHours, incluir_rastreio: completionTracking,
      });
      setJobResult(data); setCompletion(null); refreshPresence();
      toast.success(`Datas aplicadas a todos os ${data.funcionarios.length} participantes. Baixe a versão concluída.`);
    } catch (e) { const detail = e.response?.data?.detail; toast.error(typeof detail === "string" ? detail : detail?.message || "Confira o histórico antes de repetir a conclusão"); }
    finally { setCompleting(false); }
  };

  const downloadZip = async () => {
    if (!jobResult?.job_id) return;
    const safe = (s) => (s || "").replace(/[\\/:*?"<>|]/g, "").trim() || "cliente";
    const fname = `Treinamento ${safe(jobResult.treinamento_nome || selectedCfg?.nome)} - ${safe(jobResult.empresa_cliente || empresa)}.zip`;
    try {
      const { data } = await api.get(`/treinamentos/emitir/${jobResult.job_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data); const a = document.createElement("a");
      a.href = url; a.download = fname; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível baixar o pacote"); }
  };

  return (
    <div className="space-y-5" data-testid="treinamentos-emissao-tab">
      {readOnly && <p role="status" className="rounded-lg border p-3 text-sm">Seu perfil permite consultar os registros e baixar documentos. Emissão e conclusão precisam de um perfil com edição.</p>}
      {renewalLoading && <p data-testid="renewal-loading" role="status" className="text-sm">Preparando renovação...</p>}
      {renewalOf && <div data-testid="renewal-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 p-4"><p className="text-sm font-medium">Nova emissão de reciclagem · histórico original preservado</p><Button type="button" variant="ghost" size="sm" data-testid="renewal-clear" onClick={() => { setRenewalOf(""); setParams({ tab: "emissao" }); }}>Emissão independente</Button></div>}
      <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-4 sm:p-5">
        <div className="font-semibold">Emissão em 4 passos</div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Escolha o treinamento e o cliente, informe as datas ou deixe em branco, adicione os participantes e confira o instrutor. Depois clique em gerar.</p>
      </div>

      {!renewalOf && <label className="flex items-center gap-2 rounded-xl border p-4"><input type="checkbox" checked={multi} disabled={generating} onChange={(e) => { setMulti(e.target.checked); setBatchResult(null); }} /> Selecionar vários treinamentos <InfoHelp label="Emissão de vários treinamentos">Preencha e adicione cada treinamento à seleção. Cada item conserva cliente, datas, participantes e instrutor. Depois gere todos e baixe um único pacote.</InfoHelp></label>}
      {!renewalOf && <div className="rounded-xl border p-4"><label className="flex items-center gap-2"><input type="checkbox" checked={blankDates} disabled={generating || !!batchResult} onChange={(e) => { setBlankDates(e.target.checked); batchKey.current = null; }} /> Deixar datas e horários em branco</label><p className="text-sm text-muted-foreground mt-2">Prepara os documentos para preencher à mão. Não registra conclusão, certificado válido ou vencimento. A carga horária e o conteúdo de cada treinamento são mantidos.</p></div>}
      {multi && blankDates && <section className="rounded-xl border p-4 space-y-3"><h3 className="font-semibold">Selecione os treinamentos ({checkedCourses.length}/20)</h3><p className="text-sm text-muted-foreground">Cliente, participantes e instrutor abaixo serão usados em todos os itens selecionados.</p><div className="max-h-72 overflow-auto space-y-2">{configs.map((course) => <label key={course.id} className="flex items-center gap-2 rounded-lg border p-3 text-sm"><input type="checkbox" checked={checkedCourses.includes(course.id)} disabled={generating || !!batchResult || (!checkedCourses.includes(course.id) && checkedCourses.length >= 20)} onChange={(e) => { setCheckedCourses((old) => e.target.checked ? [...old, course.id] : old.filter((id) => id !== course.id)); batchKey.current = null; }} />{course.nome} · {course.emitir_certificado === false ? "somente presença" : "certificados e listas"}</label>)}</div></section>}
      {multi && !blankDates && <section className="rounded-xl border p-4 space-y-3"><h3 className="font-semibold">Treinamentos selecionados ({basket.length}/20)</h3><p className="text-sm text-muted-foreground">Preencha os passos abaixo e clique em Adicionar à seleção para cada treinamento. As alterações no formulário não modificam os itens já adicionados.</p>{basket.map((item, index) => <div key={index} className="rounded-lg border p-3 text-sm"><strong>{item.nome}</strong><p>{item.payload.empresa_cliente} · {item.payload.funcionarios.length} participantes · {item.presenceOnly ? "Somente presença" : "Certificados e listas"}</p><p>Datas: {item.payload.data_inicio ? `Dias úteis a partir de ${item.payload.data_inicio}` : item.payload.datas_manuais.join(" · ")}</p><details><summary className="cursor-pointer">Conferir participantes e instrutor</summary><p>{item.payload.funcionarios.join(", ")}</p><p>{item.payload.instrutor_nome}</p><p>Horários por dia: {item.payload.horarios_por_dia.map((h, i) => `Dia ${i + 1}: ${h.inicio || "em branco"} a ${h.fim || "em branco"}`).join(" · ")}</p></details><Button variant="ghost" size="sm" disabled={generating || !!batchResult} onClick={() => { setBasket((old) => old.filter((_, i) => i !== index)); batchKey.current = null; }}>Remover {item.nome}</Button></div>)}</section>}
      <div className="grid gap-4 xl:grid-cols-2">
        <section className="space-y-4 rounded-2xl border border-border/75 bg-card p-4 sm:p-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">Passo 1</div>
            <h3 className="mt-1 text-lg font-semibold">Treinamento e cliente</h3>
          </div>
          <div hidden={multi && blankDates}>
            <TrainingLookup kind="courses" label="Treinamento *" testId="emissao-treinamento" value={treinamentoId} selectedLabel={selectedCfg?.nome} disabled={!!renewalOf || renewalLoading} onChange={(row) => { setTreinamentoId(row?.id || ""); if (row) setConfigs((old) => [...old.filter((c) => c.id !== row.id), row]); }} />
            {selectedCfg && (
              <div className="text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                {presenceOnly && <Badge variant="secondary">Somente lista de presença · {selectedCfg.duracao_minutos || 15} min</Badge>}
                <span>Duração: <span className="text-foreground font-mono font-semibold">{nDays} dia(s)</span></span>
                {isCipa && grauRisco && effCarga !== selectedCfg.carga_horaria_total && <span className="text-primary font-medium" data-testid="cipa-auto-adjust-hint">CIPA ajustada para {effCarga}h (GR {grauRisco})</span>}
              </div>
            )}
          </div>
          <div>
            <TrainingLookup kind="clients" label="Cliente cadastrado" testId="emissao-cliente" value={clienteId} selectedLabel={clientes.find((c) => c.id === clienteId)?.razao_social} disabled={!!renewalOf || renewalLoading} onChange={(row) => { setClienteId(row?.id || ""); if (row) { setClientes((old) => [...old.filter((c) => c.id !== row.id), row]); setEmpresa(row.razao_social || ""); setMunicipioEmpresa(row.municipio || row.cidade || ""); if (row.grau_risco) setGrauRisco(String(row.grau_risco)); } }} />
          </div>
          <div>
            <Label>Empresa cliente *</Label>
            <Input value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Nome da empresa" data-testid="emissao-empresa" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            <div><Label>Município da empresa</Label><Input value={municipioEmpresa} onChange={(e) => setMunicipioEmpresa(e.target.value)} placeholder="Nova Friburgo" data-testid="emissao-municipio-empresa" /></div>
            <div><Label>Município de emissão</Label><Input value={municipioEmissao} onChange={(e) => setMunicipioEmissao(e.target.value)} placeholder="Opcional" data-testid="emissao-municipio-emissao" /></div>
            <div>
              <Label>Grau de risco {isCipa && <span className="text-primary">(CIPA)</span>}</Label>
              <select value={grauRisco} onChange={(e) => setGrauRisco(e.target.value)} className="w-full h-10 px-3 rounded border border-input bg-background text-sm" data-testid="emissao-grau-risco">
                <option value="">Não informado</option><option value="1">GR 1 (CIPA 8h)</option><option value="2">GR 2 (CIPA 12h)</option><option value="3">GR 3 (CIPA 16h)</option><option value="4">GR 4 (CIPA 20h)</option>
              </select>
            </div>
          </div>
        </section>

        <section hidden={blankDates} className="space-y-4 rounded-2xl border border-border/75 bg-card p-4 sm:p-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">Passo 2</div>
            <h3 className="mt-1 text-lg font-semibold">Datas do treinamento</h3>
          </div>
          <div className="flex flex-col gap-2 text-sm sm:flex-row sm:gap-4">
            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={modoDias === "uteis"} onChange={() => setModoDias("uteis")} data-testid="emissao-modo-uteis" /> Dias úteis a partir da data inicial</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="radio" checked={modoDias === "manual"} onChange={() => setModoDias("manual")} data-testid="emissao-modo-manual" /> Escolher as datas</label>
          </div>
          {modoDias === "uteis" && (
            <div><Label>Data de início</Label><Input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} data-testid="emissao-data-inicio" /><p className="text-xs text-muted-foreground mt-1">Sábados e domingos serão pulados automaticamente.</p></div>
          )}
          {modoDias === "manual" && nDays > 0 && (
            <div className="space-y-3">
              {datasManuais.map((d, i) => (
                <div key={`dia-${i}`} className="grid gap-2 rounded-xl border border-border/60 p-3 sm:grid-cols-[1fr_120px_120px] sm:items-end">
                  <div><Label>Dia {i + 1} de {nDays}</Label><Input type="date" value={d} onChange={(e) => updateDataManual(i, e.target.value)} data-testid={`emissao-data-manual-${i}`} /></div>
                  <div><Label className="text-[11px]">Início</Label><Input type="time" value={horariosPorDia[i]?.inicio || ""} onChange={(e) => updHorario(i, "inicio", e.target.value)} data-testid={`emissao-hora-inicio-${i}`} /></div>
                  <div><Label className="text-[11px]">Término</Label><Input type="time" value={horariosPorDia[i]?.fim || ""} onChange={(e) => updHorario(i, "fim", e.target.value)} data-testid={`emissao-hora-fim-${i}`} /></div>
                </div>
              ))}
            </div>
          )}
          {modoDias === "uteis" && nDays > 0 && (
            <details className="rounded-xl border border-border/60 bg-muted/10 p-3">
              <summary className="cursor-pointer text-sm font-medium">Informar horários por dia (opcional)</summary>
              <div className="mt-3 space-y-2">
                {Array.from({ length: nDays }).map((_, i) => (
                  <div key={`horario-uteis-${i}`} className="grid gap-2 sm:grid-cols-[70px_1fr_1fr] sm:items-center text-sm">
                    <span className="text-muted-foreground">Dia {i + 1}</span>
                    <Input type="time" value={horariosPorDia[i]?.inicio || ""} onChange={(e) => updHorario(i, "inicio", e.target.value)} data-testid={`emissao-hora-inicio-${i}`} />
                    <Input type="time" value={horariosPorDia[i]?.fim || ""} onChange={(e) => updHorario(i, "fim", e.target.value)} data-testid={`emissao-hora-fim-${i}`} />
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>
      </div>

      <section className="space-y-4 rounded-2xl border border-border/75 bg-card p-4 sm:p-5">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div><div className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">Passo 3</div><h3 className="mt-1 text-lg font-semibold">Participantes</h3></div>
          {funcionarios.length > 0 && <span className="text-sm font-medium">{funcionarios.length} participante(s)</span>}
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <label className="cursor-pointer inline-flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg text-sm hover:bg-muted/30" data-testid="emissao-upload-label">
            <Upload className="w-4 h-4" />{uploading ? "Lendo planilha..." : "Importar Excel (.xlsx)"}<input type="file" accept=".xlsx" hidden onChange={handleExcelUpload} data-testid="emissao-upload-input" />
          </label>
          <span className="text-xs text-muted-foreground">ou</span>
          <Input placeholder="Nome do participante" value={funcionarioManual} onChange={(e) => setFuncionarioManual(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFuncionarioManual())} className="sm:max-w-xs" data-testid="emissao-funcionario-input" />
          <Button type="button" variant="outline" onClick={addFuncionarioManual} data-testid="emissao-funcionario-add"><Plus className="w-3 h-3 mr-1" /> Adicionar</Button>
        </div>
        {funcionarios.length > 0 && (
          <div className="border border-border rounded-xl overflow-auto max-h-60" data-testid="emissao-funcionarios-list">
            <ul className="divide-y divide-border">
              {funcionarios.map((n, i) => (
                <li key={`${n}-${i}`} className="flex items-center justify-between px-3 py-2 text-sm"><span className="min-w-0 truncate"><span className="text-muted-foreground font-mono text-xs mr-2">{i + 1}.</span>{n}</span><Button variant="ghost" size="sm" onClick={() => removeFuncionario(i)} aria-label={`Remover ${n}`} data-testid={`emissao-funcionario-remove-${i}`}><Trash2 className="w-3 h-3" /></Button></li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <details className="rounded-2xl border border-border/75 bg-card p-4 sm:p-5">
        <summary className="cursor-pointer list-none">
          <div className="flex items-center justify-between gap-3"><div><div className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">Passo 4</div><h3 className="mt-1 text-lg font-semibold">Instrutor e credenciais</h3><p className="mt-1 text-sm text-muted-foreground">Os dados padrão já estão preenchidos. Abra apenas se precisar conferir ou alterar.</p></div><Settings2 className="size-5 shrink-0 text-muted-foreground" /></div>
        </summary>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div><Label>Nome</Label><Input value={instrutorNome} onChange={(e) => setInstrutorNome(e.target.value)} data-testid="emissao-instrutor-nome" /></div>
          <div><Label>Credenciais</Label><Textarea rows={3} value={instrutorCred} onChange={(e) => setInstrutorCred(e.target.value)} data-testid="emissao-instrutor-cred" /></div>
        </div>
      </details>

      <div hidden={presenceOnly || blankDates} className="rounded-xl border border-border p-4">
        <label className="flex items-center gap-3"><input type="checkbox" checked={incluirRastreio} onChange={(e) => setIncluirRastreio(e.target.checked)} className="size-4" />Incluir link e QR code de validação no certificado</label>
        <InfoHelp label="Rastreio no certificado">Opcional e desmarcado por padrão. Inclui o endereço público e o QR code para consultar a autenticidade. Sem marcar, esses elementos não são adicionados ao certificado; o registro interno permanece.</InfoHelp>
      </div>

      <div className="sticky bottom-3 z-20 flex flex-col gap-3 rounded-2xl border border-border/80 bg-background/95 p-3 shadow-xl backdrop-blur sm:flex-row sm:items-center sm:justify-between" data-testid="emissao-action-bar">
        <div className="text-sm">
          <span className="font-semibold">Pronto para gerar</span>
          <span className="ml-2 text-muted-foreground">{selectedCfg ? `${selectedCfg.nome} · ${nDays} dia(s)` : "Selecione um treinamento"} · {funcionarios.length} participante(s)</span>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {jobResult && <Button variant="outline" onClick={downloadZip} data-testid="emissao-download-btn"><FileArchive className="w-4 h-4 mr-2" /> Baixar pacote</Button>}
          {multi && !blankDates && <Button variant="outline" onClick={addToBasket} disabled={generating || !!batchResult}>Adicionar à seleção</Button>}
          <Button onClick={emitir} disabled={readOnly || generating || renewalLoading || (multi && (!!batchResult || !selectionCount))} data-testid="emissao-generate-btn">{generating ? "Gerando..." : multi ? `Gerar ${selectionCount} treinamentos selecionados` : blankDates ? "Preparar documentos em branco" : presenceOnly ? "Gerar somente listas de presença" : "Gerar certificados e listas"}</Button>
        </div>
      </div>

      {batchResult && <section className="rounded-xl border p-4 space-y-3" role="status"><h3 className="font-semibold">{batchResult.complete ? "Lote concluído" : "Lote precisa de revisão"}</h3>{batchResult.items.map((item) => <p key={item.item}>Treinamento {item.item}: {item.status === "gerado" ? "gerado" : "interrompido; consulte o histórico"}</p>)}{batchResult.items.some((item) => item.status === "gerado") && <Button onClick={() => downloadPackage(`/treinamentos/emitir-lote/${batchResult.batch_id}/download`, "Treinamentos.zip")}>Baixar todos os pacotes gerados</Button>}<Button variant="outline" onClick={() => { setBasket([]); setCheckedCourses([]); setBatchResult(null); batchKey.current = null; }}>Começar uma nova seleção</Button></section>}
      <details className="rounded-xl border p-4"><summary className="cursor-pointer font-medium">Listas de presença e documentos preparados ({presenceRecords.length})</summary><p className="text-xs text-muted-foreground mt-2">Últimos 50 registros. Não entram nos vencimentos de certificados.</p>{presenceRecords.map((record) => <div key={record.job_id} className="py-3 border-b text-sm"><strong>{record.treinamento_nome}</strong><p>{record.cliente_nome} · {record.finalized_job_id ? "Concluído; versão original preservada" : record.preparado ? "Preparado para preencher à mão" : record.data_base} · {record.participantes_count} participantes</p><Button variant="outline" size="sm" onClick={() => downloadPackage(`/treinamentos/emitir/${record.job_id}/download`, "Listas_de_presenca.zip")}>Baixar documentos originais</Button>{record.preparado && !record.finalized_job_id && !readOnly && <Button size="sm" className="ml-2" onClick={() => openCompletion(record)}>Informar datas e concluir</Button>}{record.finalized_job_id && <Button variant="outline" size="sm" className="ml-2" onClick={() => downloadPackage(`/treinamentos/emitir/${record.finalized_job_id}/download`, "Treinamento_concluido.zip")}>Baixar versão concluída</Button>}</div>)}</details>
      <Dialog open={!!completion} onOpenChange={(open) => { if (!open && !completing) setCompletion(null); }}><DialogContent className="max-h-[90vh] overflow-auto"><DialogHeader><DialogTitle>Informar datas e concluir</DialogTitle><DialogDescription>{completion?.treinamento_nome} · {completion?.cliente_nome}. As datas e os horários serão aplicados a todos os {completion?.participantes?.length || 0} participantes. O documento original será preservado.</DialogDescription></DialogHeader>
        <details><summary className="cursor-pointer text-sm">Conferir participantes</summary><p className="text-sm mt-2">{completion?.participantes?.join(", ")}</p></details>
        {completionDates.map((date, index) => <div key={index} className="grid gap-2 sm:grid-cols-3"><div><Label htmlFor={`concluir-data-${index}`}>Data do dia {index + 1}</Label><Input id={`concluir-data-${index}`} type="date" value={date} onChange={(e) => setCompletionDates((old) => old.map((d, i) => i === index ? e.target.value : d))} /></div><div><Label htmlFor={`concluir-inicio-${index}`}>Início</Label><Input id={`concluir-inicio-${index}`} type="time" value={completionHours[index]?.inicio || ""} onChange={(e) => setCompletionHours((old) => old.map((h, i) => i === index ? { ...h, inicio: e.target.value } : h))} /></div><div><Label htmlFor={`concluir-fim-${index}`}>Término</Label><Input id={`concluir-fim-${index}`} type="time" value={completionHours[index]?.fim || ""} onChange={(e) => setCompletionHours((old) => old.map((h, i) => i === index ? { ...h, fim: e.target.value } : h))} /></div></div>)}
        {completion?.emitir_certificado !== false && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={completionTracking} onChange={(e) => setCompletionTracking(e.target.checked)} /> Incluir link e QR code nos certificados concluídos</label>}
        <DialogFooter><Button variant="outline" disabled={completing} onClick={() => setCompletion(null)}>Cancelar</Button><Button disabled={completing} onClick={completeTraining}>{completing ? "Concluindo..." : "Aplicar a todos e gerar documentos"}</Button></DialogFooter></DialogContent></Dialog>
      {jobResult && (
        <div className="rounded-2xl border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="emissao-result">
          <div className="flex items-center gap-2"><Award className="w-5 h-5 text-primary" /><div className="font-semibold">{jobResult.preparado ? "Documentos preparados para preencher à mão" : "Pacote gerado com sucesso"}</div></div>
          <div className="grid gap-4 text-sm md:grid-cols-3">
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Dias de treinamento</div><div className="font-mono">{jobResult.n_dias}</div></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Certificados</div><div className="font-mono">{jobResult.cert_filename && !jobResult.preparado ? jobResult.funcionarios.length : 0}</div></div>
            <div><div className="text-xs uppercase tracking-wider text-muted-foreground">Horas por dia</div><div className="font-mono">{jobResult.horas_por_dia.join(" · ")}</div></div>
          </div>
          <div><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Conteúdo por dia</div><ol className="text-xs space-y-1 list-decimal pl-5">{jobResult.conteudo_por_dia.map((c, i) => <li key={jobResult.dias[i] || `dia-${i}`}><span className="text-muted-foreground">{jobResult.dias[i]}:</span> {c || "—"}</li>)}</ol></div>
          <Button size="sm" onClick={downloadZip} data-testid="emissao-download-btn-inline"><Download className="w-4 h-4 mr-2" /> Baixar pacote .zip</Button>
        </div>
      )}
    </div>
  );
}

export default function TreinamentosPage() {
  const { isAdmin } = useAuth();
  const [params] = useSearchParams();
  const requestedTab = params.get("tab");
  const [tab, setTab] = useState(requestedTab === "emissao" ? "emissao" : "validade");
  useEffect(() => { if (requestedTab === "emissao") setTab("emissao"); }, [requestedTab]);
  return (
    <>
      <Header title="Treinamentos" />
      <div className="page-container space-y-5" data-testid="treinamentos-page">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className={`grid w-full max-w-xl ${isAdmin ? "grid-cols-3" : "grid-cols-2"}`}>
            <TabsTrigger value="validade" data-testid="tab-validade"><Calendar className="hidden size-4 sm:mr-2 sm:block" />Validade</TabsTrigger>
            <TabsTrigger value="emissao" data-testid="tab-emissao"><Award className="hidden size-4 sm:mr-2 sm:block" />Emitir</TabsTrigger>
            {isAdmin && <TabsTrigger value="configs" data-testid="tab-configs"><Settings2 className="hidden size-4 sm:mr-2 sm:block" />Modelos</TabsTrigger>}
          </TabsList>
          <TabsContent value="validade" className="mt-5"><TrainingValidityPanel /></TabsContent>
          <TabsContent value="emissao" className="mt-5"><EmissaoTab /></TabsContent>
          {isAdmin && <TabsContent value="configs" className="mt-5"><ConfigsTab /></TabsContent>}
        </Tabs>
      </div>
    </>
  );
}

